/**
 * EclipseTimerCard - Circular orbit phase visualization
 *
 * Shows the spacecraft's position in its orbital day/night cycle with
 * visual sectors for sunlight, penumbra, and umbra (eclipse).
 *
 * Astro UX Compliance:
 * - Uses official Astro status colors
 * - Sentence-case labels
 * - Tabular numbers for data
 * - Roboto/Roboto Mono typography
 * - 4px-based spacing
 */

import React, { useMemo } from "react";
import type { EclipseInfo } from "../types";
import { useTheme } from "../theme";

export interface EclipseTimerCardProps {
  /** Eclipse information from API */
  eclipse: EclipseInfo;
  /** Orbital period in minutes */
  orbitalPeriodMinutes: number;
  /** Current phase in orbit (0-1) */
  orbitPhase?: number;
  /** Compact mode for smaller displays */
  compact?: boolean;
  /** Custom class name */
  className?: string;
}

export const EclipseTimerCard: React.FC<EclipseTimerCardProps> = ({
  eclipse,
  orbitalPeriodMinutes,
  orbitPhase = 0,
  compact = false,
  className = "",
}) => {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  // Use transparent background with blur for transparent themes (matching Timeline)
  const cardBg = isTransparentTheme
    ? "transparent"
    : tokens.colors.background.surface;
  const cardGlass = isTransparentTheme
    ? {
        backdropFilter: "blur(12px)" as const,
        WebkitBackdropFilter: "blur(12px)" as const,
      }
    : {};

  // Medium-soft colors for eclipse phases (readable, not too pale)
  const ECLIPSE_COLORS = {
    sunlight: "#ffd54f",
    penumbra: "#78909c",
    umbra: "#546e7a",
  };
  const { sunlightArc, penumbraArc, umbraArc } = useMemo(() => {
    const totalPeriod = orbitalPeriodMinutes * 60; // seconds
    const sunlight = eclipse.sunlightDuration / totalPeriod;
    const umbraSec = eclipse.umbraDuration ?? eclipse.eclipseDuration * 0.5;
    const penumbraSec =
      eclipse.penumbraDuration ?? eclipse.eclipseDuration * 0.5;
    const umbra = umbraSec / totalPeriod;
    const penumbra = penumbraSec / totalPeriod;
    return {
      sunlightArc: sunlight * 100,
      penumbraArc: penumbra * 100,
      umbraArc: umbra * 100,
    };
  }, [eclipse, orbitalPeriodMinutes]);

  // Format time for display (Astro: tabular numbers)
  const formatTime = (seconds: number): string => {
    if (seconds < 0) return "--:--";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Calculate SVG arc parameters
  const radius = 45;
  const circumference = 2 * Math.PI * radius;

  const sunlightDash = (sunlightArc / 100) * circumference;
  const penumbraDash = (penumbraArc / 100) * circumference;
  const umbraDash = (umbraArc / 100) * circumference;

  // Cursor rotation based on orbit phase
  const cursorRotation = orbitPhase * 360 - 90; // -90 to start at top

  const sizeStyle: React.CSSProperties = compact
    ? { width: "128px", height: "128px" }
    : { width: "192px", height: "192px" };

  // Astro UX compliant card styling (transparent theme: glass background)
  const cardStyle: React.CSSProperties = {
    backgroundColor: cardBg,
    ...cardGlass,
    border: "none",
    borderRadius: tokens.borderRadius.lg,
    padding: tokens.spacing.md,
    fontFamily: tokens.typography.fontFamily.primary,
    color: tokens.colors.text.primary,
  };

  return (
    <div className={className} style={cardStyle}>
      {!compact && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: tokens.spacing.md,
          }}
        >
          <div>
            {/* Astro: Sentence-case labels */}
            <h3
              style={{
                fontWeight: tokens.typography.fontWeight.semibold,
                color: tokens.colors.text.primary,
                fontSize: tokens.typography.fontSize.md,
                margin: 0,
              }}
            >
              Eclipse timer
            </h3>
            <p
              style={{
                color: tokens.colors.text.tertiary,
                fontSize: tokens.typography.fontSize.xs,
                margin: 0,
                marginTop: tokens.spacing.xs,
              }}
            >
              Orbit phase
            </p>
          </div>
          {/* Status badge - Astro style */}
          <span
            style={{
              padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
              borderRadius: tokens.borderRadius.sm,
              fontSize: tokens.typography.fontSize.xxs, // 0.625rem / 10px (AstroUXDS compact)
              fontWeight: tokens.typography.fontWeight.medium,
              letterSpacing: "0.5px",
              backgroundColor: eclipse.inEclipse
                ? `${tokens.colors.status.off}20`
                : `${tokens.colors.status.caution}20`,
              color: eclipse.inEclipse
                ? tokens.colors.status.off
                : tokens.colors.status.caution,
            }}
          >
            {eclipse.inEclipse ? "Eclipse" : "Sunlit"}
          </span>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "center" }}>
        <div style={{ position: "relative", ...sizeStyle }}>
          {/* SVG Ring */}
          <svg
            viewBox="0 0 100 100"
            style={{
              width: "100%",
              height: "100%",
              transform: "rotate(-90deg)",
            }}
          >
            {/* Background ring */}
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke={tokens.colors.border.muted}
              strokeWidth="8"
            />

            {/* Sunlight sector - soft amber */}
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke={ECLIPSE_COLORS.sunlight}
              strokeWidth="8"
              strokeDasharray={`${sunlightDash} ${circumference}`}
              strokeDashoffset="0"
              strokeLinecap="round"
              style={{
                filter: `drop-shadow(0 0 8px ${ECLIPSE_COLORS.sunlight}40)`,
              }}
            />

            {/* Penumbra sector */}
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke={ECLIPSE_COLORS.penumbra}
              strokeWidth="8"
              strokeDasharray={`${penumbraDash} ${circumference}`}
              strokeDashoffset={`${-sunlightDash}`}
              strokeLinecap="round"
            />

            {/* Umbra sector - dark */}
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke={tokens.colors.background.base}
              strokeWidth="8"
              strokeDasharray={`${umbraDash} ${circumference}`}
              strokeDashoffset={`${-(sunlightDash + penumbraDash)}`}
              strokeLinecap="round"
            />
          </svg>

          {/* Position cursor */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              transition: "transform 1s",
              transform: `rotate(${cursorRotation}deg)`,
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: "50%",
                transform: "translate(-50%, -4px)",
              }}
            >
              <div
                style={{
                  width: compact ? "12px" : "16px",
                  height: compact ? "12px" : "16px",
                  backgroundColor: tokens.colors.text.primary,
                  borderRadius: "50%",
                  border: `2px solid ${tokens.colors.status.standby}`,
                  boxShadow: `0 0 12px ${tokens.colors.status.standby}`,
                }}
              />
            </div>
          </div>

          {/* Center content */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Astro: Sentence-case labels (Tier 3 compliance) */}
            <span
              style={{
                fontSize: compact ? "8px" : tokens.typography.fontSize.xs,
                color: tokens.colors.text.tertiary,
                letterSpacing: "0.5px",
              }}
            >
              {eclipse.inEclipse ? "Sunlight in" : "Eclipse in"}
            </span>
            {/* Astro: Tabular numbers */}
            <span
              style={{
                fontSize: compact
                  ? tokens.typography.fontSize.lg
                  : tokens.typography.fontSize.xxl,
                fontFamily: tokens.typography.fontFamily.mono,
                fontWeight: tokens.typography.fontWeight.bold,
                fontVariantNumeric: "tabular-nums",
                color: eclipse.inEclipse
                  ? tokens.colors.status.caution
                  : tokens.colors.text.tertiary,
              }}
            >
              {formatTime(
                eclipse.inEclipse
                  ? (eclipse.timeToSunlight ?? 0)
                  : eclipse.timeToEclipse,
              )}
            </span>
            {!compact && (
              <span
                style={{
                  fontSize: tokens.typography.fontSize.xxs, // 0.625rem / 10px (AstroUXDS compact)
                  color: tokens.colors.text.tertiary,
                  marginTop: tokens.spacing.xs,
                }}
              >
                Period: {orbitalPeriodMinutes.toFixed(1)}m
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Stats row - Astro: Sentence-case, tabular numbers */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: tokens.spacing.sm,
          marginTop: tokens.spacing.md,
          fontSize: compact
            ? tokens.typography.fontSize.xxs
            : tokens.typography.fontSize.xs, // AstroUXDS compliant
        }}
      >
        <div
          style={{
            backgroundColor: tokens.colors.background.base,
            borderRadius: tokens.borderRadius.md,
            padding: tokens.spacing.sm,
            textAlign: "center",
          }}
        >
          <div
            style={{
              color: tokens.colors.status.caution,
              fontWeight: tokens.typography.fontWeight.bold,
              fontFamily: tokens.typography.fontFamily.mono,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {Math.round(eclipse.sunlightDuration / 60)}m
          </div>
          <div style={{ color: tokens.colors.text.tertiary }}>Daylight</div>
        </div>
        <div
          style={{
            backgroundColor: tokens.colors.background.base,
            borderRadius: tokens.borderRadius.md,
            padding: tokens.spacing.sm,
            textAlign: "center",
          }}
        >
          <div
            style={{
              color: tokens.colors.text.tertiary,
              fontWeight: tokens.typography.fontWeight.bold,
              fontFamily: tokens.typography.fontFamily.mono,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {Math.round(
              ((eclipse.umbraDuration ?? 0) + (eclipse.penumbraDuration ?? 0)) /
                60,
            )}
            m
          </div>
          <div style={{ color: tokens.colors.text.tertiary }}>Eclipse</div>
        </div>
      </div>
    </div>
  );
};

export default EclipseTimerCard;
